/**
 * Shapes returned by the backend. Kept as a hand-written mirror of the Prisma models rather
 * than generated, because the dashboard only needs the fields it renders.
 */

export const LEAD_STATUSES = [
  'NEW',
  'ENRICHED',
  'QUEUED',
  'DRAFTED',
  'SCHEDULED',
  'CONTACTED',
  'REPLIED',
  'INTERESTED',
  'WON',
  'LOST',
  'BOUNCED',
  'UNSUBSCRIBED',
  'DISQUALIFIED',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const EMAIL_STATUSES = [
  'DRAFT',
  'SCHEDULED',
  'SENDING',
  'SENT',
  'FAILED',
  'CANCELLED',
  'RECEIVED',
] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export const JOB_STATUSES = ['PENDING', 'CLAIMED', 'DONE', 'FAILED', 'CANCELLED'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const JOB_TYPES = ['RESEARCH', 'OUTREACH', 'ENRICH', 'CONTENT', 'CUSTOM'] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const CAMPAIGN_STATUSES = ['QUEUED', 'RUNNING', 'PAUSED', 'DONE', 'CANCELLED'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const USER_ROLES = ['ADMIN', 'MEMBER'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const BLOG_TOPIC_STATUSES = [
  'PENDING',
  'QUEUED',
  'DRAFTING',
  'DRAFTED',
  'PUBLISHED',
  'SKIPPED',
  'ARCHIVED',
] as const;
export type BlogTopicStatus = (typeof BLOG_TOPIC_STATUSES)[number];

export const BLOG_POST_STATUSES = [
  'DRAFT',
  'SCHEDULED',
  'PUBLISHING',
  'PUBLISHED',
  'SUPERSEDED',
  'FAILED',
  'CANCELLED',
] as const;
export type BlogPostStatus = (typeof BLOG_POST_STATUSES)[number];

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
}

export interface AuthStatus {
  needsSetup: boolean;
  userCount: number;
}

export interface Session {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  scope: string;
  user: User;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const PROJECT_KINDS = ['OUTREACH', 'CONTENT'] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

export interface Project {
  id: string;
  name: string;
  slug: string;
  /** OUTREACH (GTM) or CONTENT — decides the menu and which features are available. */
  kind: ProjectKind;
  description: string | null;
  productContext: string | null;
  researchPrompt: string | null;
  outreachPrompt: string | null;
  contentPrompt?: string | null;
  websiteUrl: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  stats?: {
    leads: number;
    leadTypes: number;
    campaigns: number;
    topics?: number;
    posts?: number;
    emailsSent: number;
    repliesReceived: number;
  };
  leadTypes?: LeadType[];
  mailboxes?: Mailbox[];
  _count?: {
    leads: number;
    campaigns: number;
    jobs: number;
    blogTopics?: number;
    blogPosts?: number;
  };
}

export interface LeadType {
  id: string;
  projectId: string;
  name: string;
  slug: string;
  description: string | null;
  researchPrompt: string;
  outreachPrompt: string | null;
  icp: string | null;
  disqualifiers: string | null;
  sourcesHint: string | null;
  targetCount: number;
  color: string;
  isActive: boolean;
  createdAt: string;
  stats?: { leads: number; contacted: number; targetCount: number; progress: number };
}

export interface Lead {
  id: string;
  projectId: string;
  leadTypeId: string | null;
  companyName: string;
  contactName: string | null;
  title: string | null;
  email: string | null;
  emailStatus: string;
  phone: string | null;
  website: string | null;
  domain: string | null;
  linkedinUrl: string | null;
  otherUrl: string | null;
  country: string | null;
  city: string | null;
  language: string | null;
  industry: string | null;
  companySize: string | null;
  source: string | null;
  sourceUrl: string | null;
  discoveryQuery: string | null;
  score: number | null;
  rationale: string | null;
  notes: string | null;
  tags: string | null;
  customFields: string | null;
  status: LeadStatus;
  dedupeKey: string;
  timesContacted: number;
  lastContactedAt: string | null;
  repliedAt: string | null;
  createdAt: string;
  updatedAt: string;
  leadType?: { id: string; name: string; color: string } | null;
  emails?: EmailSummary[];
  outreachTasks?: Array<{
    id: string;
    status: string;
    createdAt: string;
    campaign: { id: string; name: string; status: string };
  }>;
}

export interface EmailSummary {
  id: string;
  direction: 'OUTBOUND' | 'INBOUND';
  status: EmailStatus;
  subject: string;
  snippet: string | null;
  scheduledAt: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  openCount: number;
  clickCount: number;
  createdAt: string;
}

export interface Email extends EmailSummary {
  projectId: string;
  leadId: string | null;
  mailboxId: string;
  campaignId: string | null;
  bodyHtml: string;
  bodyText: string;
  toEmail: string;
  toName: string | null;
  fromEmail: string;
  fromName: string | null;
  cc: string | null;
  bcc: string | null;
  error: string | null;
  attempts: number;
  sequenceStep: number;
  trackingToken: string | null;
  approvedAt: string | null;
  lead?: { id: string; companyName: string; contactName: string | null; status: string } | null;
  campaign?: { id: string; name: string } | null;
  mailbox?: { id: string; label: string; fromEmail: string; fromName: string } | null;
}

export interface Mailbox {
  id: string;
  projectId: string;
  label: string;
  fromName: string;
  fromEmail: string;
  replyTo: string | null;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  imapHost: string | null;
  imapPort: number;
  imapSecure: boolean;
  imapUser: string | null;
  imapFolder: string;
  saveToSent: boolean;
  sentFolder: string | null;
  signatureHtml: string | null;
  dailyLimit: number;
  hourlyLimit: number;
  minGapSeconds: number;
  sendWindowStart: number;
  sendWindowEnd: number;
  sendDays: string;
  timezone: string;
  trackOpens: boolean;
  trackClicks: boolean;
  isDefault: boolean;
  isActive: boolean;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  lastSendError: string | null;
  hasSmtpPassword: boolean;
  hasImapPassword: boolean;
}

export interface Campaign {
  id: string;
  projectId: string;
  leadTypeId: string | null;
  mailboxId: string | null;
  name: string;
  instructions: string | null;
  status: CampaignStatus;
  requireApproval: boolean;
  scheduledStartAt: string | null;
  spacingSeconds: number;
  createdAt: string;
  leadType?: { id: string; name: string; color: string } | null;
  mailbox?: { id: string; label: string; fromEmail: string } | null;
  progress?: { total: number; pending: number; drafted: number; sent: number; skipped: number };
  tasks?: OutreachTask[];
  jobId?: string;
  taskCount?: number;
}

export interface OutreachTask {
  id: string;
  status: string;
  error: string | null;
  attempts: number;
  claimedBy: string | null;
  emailId: string | null;
  createdAt: string;
  lead: { id: string; companyName: string; contactName: string | null; email: string | null; status: string };
}

export interface Job {
  id: string;
  projectId: string;
  leadTypeId: string | null;
  campaignId: string | null;
  contentBatchId: string | null;
  type: JobType;
  status: JobStatus;
  priority: number;
  payload: string | Record<string, unknown>;
  result: string | null;
  claimedBy: string | null;
  claimedAt: string | null;
  leaseExpiresAt: string | null;
  heartbeatAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  attempts: number;
  maxAttempts: number;
  itemsFound: number;
  itemsCreated: number;
  itemsSkipped: number;
  createdAt: string;
  leadType?: { id: string; name: string; color?: string } | null;
  campaign?: { id: string; name: string; status?: string } | null;
  contentBatch?: { id: string; name: string } | null;
  project?: { id: string; name: string } | null;
  logs?: JobLog[];
  _count?: { logs: number };
}

export interface JobLog {
  id: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
  message: string;
  data: string | null;
  createdAt: string;
}

/** STRUCTURE: a subject + body skeleton to follow. PROMPT: instructions only, no subject. */
export type EmailTemplateKind = 'STRUCTURE' | 'PROMPT';

export interface EmailTemplate {
  id: string;
  projectId: string;
  leadTypeId: string | null;
  name: string;
  kind: EmailTemplateKind;
  /** Null on PROMPT templates — the agent writes the subject itself. */
  subject: string | null;
  /** The skeleton on a STRUCTURE template, the instructions on a PROMPT one. */
  body: string;
  notes: string | null;
  isDefault: boolean;
  leadType?: { id: string; name: string; color: string } | null;
}

export interface Suppression {
  id: string;
  projectId: string;
  type: 'EMAIL' | 'DOMAIN';
  value: string;
  reason: string | null;
  createdAt: string;
}

export interface SearchQueryRecord {
  id: string;
  query: string;
  source: string | null;
  resultsFound: number;
  newLeads: number;
  notes: string | null;
  createdAt: string;
  leadTypeId: string | null;
}

export interface ActivityEvent {
  id: string;
  projectId: string | null;
  type: string;
  entityType: string | null;
  entityId: string | null;
  message: string;
  createdAt: string;
}

export interface StatsOverview {
  totals: {
    leads: number;
    emailsSent: number;
    repliesReceived: number;
    leadsReplied: number;
    drafts: number;
    scheduled: number;
    replyRate: number;
    openRate: number;
    clicks: number;
  };
  leadsByStatus: Record<string, number>;
  leadsByType: Array<{
    leadTypeId: string | null;
    name: string;
    color: string;
    targetCount: number;
    count: number;
  }>;
  emailsByStatus: Record<string, number>;
  jobsByStatus: Record<string, number>;
  activeCampaigns: Array<{ id: string; name: string; status: string; createdAt: string; projectId: string }>;
  timeline: Array<{ date: string; leads: number; emails: number }>;
  activity: ActivityEvent[];
}

export interface OAuthClientSummary {
  id: string;
  clientId: string;
  name: string;
  redirectUris: string[];
  grantTypes: string[];
  scope: string;
  isPublic: boolean;
  dynamic: boolean;
  disabled: boolean;
  hasRecoverableSecret: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  activeTokens?: number;
}

export interface SessionInfo {
  subject: string;
  scopes: string[];
  kind: string;
  clientId?: string;
  user: User | null;
  role: UserRole | null;
  warnings: {
    mustChangePassword: boolean;
    ephemeralJwtSecret: boolean;
    emailDryRun: boolean;
  };
  config: {
    publicUrl: string;
    mcpEndpoint: string;
    emailDryRun: boolean;
    emailAllowlist: string[];
  };
}

export interface BulkLeadResult {
  summary: { received: number; created: number; duplicates: number; suppressed: number; invalid: number };
  created: Array<{ id: string; companyName: string; email: string | null }>;
  duplicates: Array<{ companyName: string; dedupeKey: string; enrichedFields: string[] }>;
  suppressed: Array<{ dedupeKey: string; reason: string }>;
  invalid: Array<{ companyName: string | null; reason: string }>;
}

export interface MailboxTestResult {
  smtp: { ok: boolean; error?: string };
  imap: { ok: boolean; error?: string; messages?: number };
  testSend?: { ok: boolean; error?: string; messageId?: string };
}

// ---------------------------------------------------------------------------
// Content creator: topics, channels, batches, posts
// ---------------------------------------------------------------------------

export interface BlogTopic {
  id: string;
  projectId: string;
  title: string;
  angle: string | null;
  keywords: string | null;
  language: string | null;
  priority: number | null;
  status: BlogTopicStatus;
  tags: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { posts: number; tasks: number };
}

export interface ContentChannel {
  id: string;
  projectId: string;
  label: string;
  provider: 'GHOST';
  ghostAdminUrl: string | null;
  hasGhostKey: boolean;
  hasPexelsKey: boolean;
  hasUnsplashKey: boolean;
  siteUrl: string | null;
  defaultPostStatus: 'draft' | 'published';
  embedSnippet: string | null;
  defaultTags: string | null;
  minSeoScore: number;
  language: string | null;
  dailyLimit: number;
  publishWindowStart: number;
  publishWindowEnd: number;
  publishDays: string;
  timezone: string;
  isDefault: boolean;
  isActive: boolean;
  lastPublishAt: string | null;
  lastPublishError: string | null;
}

/** The key is write-only, so it never comes back on the entity — only on create/update. */
export interface ContentChannelTestResult {
  ghost: { ok: boolean; detail: string };
}

export interface ContentBatch {
  id: string;
  projectId: string;
  channelId: string | null;
  name: string;
  instructions: string | null;
  language: string | null;
  requireApproval: boolean;
  minSeoScore: number | null;
  status: string;
  createdAt: string;
  channel?: { id: string; label: string } | null;
  taskCount?: number;
  jobId?: string;
}

/** List projection returned by GET /projects/:projectId/posts. */
export interface BlogPost {
  id: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  tags: string | null;
  status: BlogPostStatus;
  seoScore: number | null;
  heroImageUrl: string | null;
  externalUrl: string | null;
  revisesPostId: string | null;
  publishedAt: string | null;
  scheduledAt: string | null;
  approvedAt: string | null;
  language: string | null;
  topic: { id: string; title: string } | null;
  channel: { id: string; label: string } | null;
  createdAt: string;
}

/** One SEO/GEO checklist item, parsed out of BlogPostDetail.seoChecklist. */
export interface SeoCheck {
  id: string;
  label: string;
  ok: boolean;
  detail?: string;
  weight: number;
}

/** Full post returned by GET /posts/:id. */
export interface BlogPostDetail extends BlogPost {
  structuredJson: string | null;
  bodyHtml: string;
  metaDescription: string | null;
  /** JSON string of SeoCheck[] — parse before rendering. */
  seoChecklist: string | null;
  agentNotes: string | null;
  channel: { id: string; label: string; siteUrl: string | null } | null;
  batch: { id: string; name: string } | null;
  revises: { id: string; title: string; status: string; externalUrl: string | null } | null;
  revisions: Array<{ id: string; status: string; externalUrl: string | null }>;
}

export interface BulkTopicsResult {
  summary: { received: number; created: number; duplicates: number; invalid: number };
  created: Array<{ id: string; title: string }>;
  duplicates: Array<{ title: string }>;
  invalid: Array<{ title: string | null; reason: string }>;
}
